/**
 * Reading a JWT's payload in the browser, for display only.
 *
 * **Nothing here verifies anything, and nothing here may decide what a user is
 * allowed to do.** The signature cannot be checked without the pool's public
 * keys, and checking it on the attacker's side of the boundary would prove
 * nothing anyway. Every real refusal happens at the API: the JWT authorizer
 * verifies the token, and `withTenantPath` compares the path against the claim.
 * A front end that hides a button is a courtesy; the 403 is the control.
 *
 * Separated from `session.ts` so it can be tested without a Cognito pool. The
 * base64url handling below is the reason that is worth doing — a decoder that
 * silently returns `{}` makes every signed-in user look tenant-less, which
 * renders as "waiting for approval" for people who were approved.
 */

export interface TokenClaims {
  readonly email: string | undefined;
  readonly tenantId: string | undefined;
  readonly tenantStatus: string | undefined;
}

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value !== "" ? value : undefined;

/** Decodes the payload segment, or returns `{}` for anything unreadable. */
export const readPayload = (idToken: string): Record<string, unknown> => {
  const segment = idToken.split(".")[1];
  if (segment === undefined || segment === "") return {};

  try {
    // base64url → base64. `atob` rejects `-` and `_`, and browsers disagree
    // about missing padding, so both are normalised rather than hoped about.
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );

    /*
     * `atob` returns a *binary string* — one character per byte — so feeding
     * it straight to `JSON.parse` mangles every multi-byte character. A JWT
     * payload is UTF-8, and Cognito will happily carry a name or an
     * internationalised email address through it. The bytes have to be
     * reassembled and decoded, not read as if each byte were a character.
     *
     * The first version of this file did exactly that and turned "ÿ" into
     * "Ã¿". It was caught by a test rather than by a user, which is the only
     * reason it is a comment and not an incident.
     */
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
    // A JWT payload is a JSON object. An array or a bare string parses fine and
    // would then be indexed as if it were claims.
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

/**
 * The three claims this shell renders from.
 *
 * The names match `api/src/lib/http/auth.ts` — `TENANT_CLAIM` and
 * `TENANT_STATUS_CLAIM`. They are written out rather than imported because
 * that module is server code; if a third place ever needs them, move them into
 * `domain` and import them the way `steps.ts` is imported.
 */
export const readClaims = (idToken: string): TokenClaims => {
  const payload = readPayload(idToken);
  return {
    email: asString(payload["email"]),
    tenantId: asString(payload["custom:tenantId"]),
    tenantStatus: asString(payload["custom:tenantStatus"]),
  };
};
